import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  SelectChangeEvent,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  Shield,
  Security,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Download,
  PictureAsPdf,
  TableChart,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ComplianceMetrics from '../components/ComplianceMetrics';

interface ComplianceDashboardData {
  organization_id: number;
  date_range: {
    start: string;
    end: string;
    days: number;
  };
  frameworks: Array<{
    name: string;
    compliance_score: number;
    score_history: Array<{
      date: string;
      score: number;
      phi_detections: number;
      phi_blocked: number;
    }>;
    metrics: {
      total_requests: number;
      phi_detections: number;
      phi_blocked: number;
      phi_redacted: number;
      breach_attempts_blocked: number;
      unauthorized_access_attempts: number;
      audit_events_logged: number;
      average_response_time_ms: number;
      detection_rate: string;
      block_rate: string;
    };
  }>;
  summary: {
    total_requests: number;
    total_phi_detections: number;
    total_phi_blocked: number;
    total_breach_attempts_blocked: number;
    overall_detection_rate: string;
    overall_block_rate: string;
    average_compliance_score: number;
  };
}

const ComplianceDashboard: React.FC = () => {
  const [selectedFramework, setSelectedFramework] = useState<string>('all');
  const [days, setDays] = useState<number>(30);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv'>('pdf');
  const { isAuthenticated } = useAuth();

  const { data: dashboardData, isLoading, error, refetch } = useQuery<ComplianceDashboardData>({
    queryKey: ['complianceDashboard', selectedFramework, days],
    queryFn: async () => {
      const params: any = { days };
      if (selectedFramework !== 'all') {
        params.framework = selectedFramework;
      }
      const response = await axios.get('/api/compliance/dashboard', { params });
      return response.data;
    },
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  });

  const handleFrameworkChange = (event: SelectChangeEvent<string>) => {
    setSelectedFramework(event.target.value);
  };

  const handleDaysChange = (event: SelectChangeEvent<number>) => {
    setDays(event.target.value as number);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const response = await axios.post('/api/compliance/export', {
        format: exportFormat,
        framework: selectedFramework === 'all' ? null : selectedFramework,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        include_rules: true,
        include_violations: true
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `compliance-report-${selectedFramework}-${Date.now()}.${exportFormat}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getScoreStatus = (score: number) => {
    if (score >= 95) return 'Excellent';
    if (score >= 85) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 50) return 'Needs Improvement';
    return 'Critical';
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load compliance dashboard data. Please try again.
        </Alert>
      </Box>
    );
  }

  const currentFramework = selectedFramework === 'all' 
    ? null 
    : dashboardData?.frameworks.find(f => f.name === selectedFramework);

  const displayScore = currentFramework 
    ? currentFramework.compliance_score 
    : dashboardData?.summary.average_compliance_score || 0;

  const displayMetrics = currentFramework 
    ? currentFramework.metrics 
    : dashboardData?.summary;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Compliance Dashboard
        </Typography>
        
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={days} onChange={handleDaysChange} label="Time Period">
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Framework</InputLabel>
            <Select 
              value={selectedFramework} 
              onChange={handleFrameworkChange}
              label="Framework"
            >
              <MenuItem value="all">All Frameworks</MenuItem>
              {dashboardData?.frameworks.map((fw) => (
                <MenuItem key={fw.name} value={fw.name}>
                  {fw.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={exportFormat === 'pdf' ? 'contained' : 'outlined'}
              onClick={() => setExportFormat('pdf')}
              startIcon={<PictureAsPdf />}
            >
              PDF
            </Button>
            <Button
              variant={exportFormat === 'csv' ? 'contained' : 'outlined'}
              onClick={() => setExportFormat('csv')}
              startIcon={<TableChart />}
            >
              CSV
            </Button>
          </ButtonGroup>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={handleExport}
            disabled={exportLoading || dashboardData?.frameworks.length === 0}
            size="small"
          >
            Export
          </Button>
        </Box>
      </Box>

      {dashboardData?.frameworks.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No compliance frameworks are currently enabled. Please enable at least one framework in Organization Settings.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Compliance Score Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Shield color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Compliance Score</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={displayScore}
                    size={120}
                    thickness={4}
                    color={getScoreColor(displayScore)}
                  />
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="h4" component="div" color="text.secondary">
                      {displayScore}
                    </Typography>
                  </Box>
                </Box>
                
                <Chip 
                  label={getScoreStatus(displayScore)}
                  color={getScoreColor(displayScore)}
                  sx={{ mt: 2 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" align="center">
                {selectedFramework === 'all' 
                  ? 'Average across all frameworks' 
                  : `${selectedFramework} compliance score`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* PHI Protection Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Security color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">PHI Protection</Typography>
              </Box>
              
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Detection Rate
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {('overall_detection_rate' in (displayMetrics || {})) 
                      ? (displayMetrics as any).overall_detection_rate 
                      : ('detection_rate' in (displayMetrics || {}))
                      ? (displayMetrics as any).detection_rate
                      : '0%'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={parseFloat(
                    ('overall_detection_rate' in (displayMetrics || {})) 
                      ? (displayMetrics as any).overall_detection_rate 
                      : ('detection_rate' in (displayMetrics || {}))
                      ? (displayMetrics as any).detection_rate
                      : '0'
                  )} 
                  color="info"
                />
              </Box>

              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Block Rate
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {('overall_block_rate' in (displayMetrics || {})) 
                      ? (displayMetrics as any).overall_block_rate 
                      : ('block_rate' in (displayMetrics || {}))
                      ? (displayMetrics as any).block_rate
                      : '0%'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={parseFloat(
                    ('overall_block_rate' in (displayMetrics || {})) 
                      ? (displayMetrics as any).overall_block_rate 
                      : ('block_rate' in (displayMetrics || {}))
                      ? (displayMetrics as any).block_rate
                      : '0'
                  )} 
                  color="success"
                />
              </Box>

              <Grid container spacing={2} mt={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Detected
                  </Typography>
                  <Typography variant="h6">
                    {('total_phi_detections' in (displayMetrics || {})) 
                      ? (displayMetrics as any).total_phi_detections 
                      : ('phi_detections' in (displayMetrics || {}))
                      ? (displayMetrics as any).phi_detections
                      : 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Blocked
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {('total_phi_blocked' in (displayMetrics || {})) 
                      ? (displayMetrics as any).total_phi_blocked 
                      : ('phi_blocked' in (displayMetrics || {}))
                      ? (displayMetrics as any).phi_blocked
                      : 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Events Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Warning color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Security Events</Typography>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Box flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    Breach Attempts Blocked
                  </Typography>
                  <Typography variant="h5">
                    {('total_breach_attempts_blocked' in (displayMetrics || {})) 
                      ? (displayMetrics as any).total_breach_attempts_blocked 
                      : ('breach_attempts_blocked' in (displayMetrics || {}))
                      ? (displayMetrics as any).breach_attempts_blocked
                      : 0}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" mb={2}>
                <ErrorIcon color="warning" sx={{ mr: 1 }} />
                <Box flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    Unauthorized Access Attempts
                  </Typography>
                  <Typography variant="h5">
                    {('unauthorized_access_attempts' in (displayMetrics || {})) 
                      ? (displayMetrics as any).unauthorized_access_attempts 
                      : 0}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center">
                <Security color="info" sx={{ mr: 1 }} />
                <Box flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    Audit Events Logged
                  </Typography>
                  <Typography variant="h5">
                    {('audit_events_logged' in (displayMetrics || {})) 
                      ? (displayMetrics as any).audit_events_logged 
                      : 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance Metrics Visualization */}
        <Grid item xs={12}>
          <ComplianceMetrics 
            framework={selectedFramework === 'all' ? null : selectedFramework}
            days={days}
            dashboardData={dashboardData}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceDashboard;
